import {Logo} from '@common/ui/navigation/navbar/logo';
import {useCustomMenu} from '@common/menus/use-custom-menu';
import {MenuItemConfig} from '@common/menus/menu-config';
import {useAuth} from '@common/auth/use-auth';
import {useIsDarkMode} from '@ui/themes/use-is-dark-mode';
import {BrandSocialIcon, detectSocialNetwork} from '@app/ui/brand-social-icons';
import {Link} from 'react-router';
import {LocaleSwitcher} from '@common/locale-switcher/locale-switcher';
import {Button} from '@ui/buttons/button';
import {Trans} from '@ui/i18n/trans';
import {DarkModeIcon} from '@ui/icons/material/DarkMode';
import {LightbulbIcon} from '@ui/icons/material/Lightbulb';
import {useSettings} from '@ui/settings/use-settings';
import {useThemeSelector} from '@ui/themes/theme-selector-context';
import clsx from 'clsx';
import {BlockArea} from '@app/appearance/block-area';
import {getAppearance} from '@app/appearance/appearance-data';

interface Props {
  className?: string;
  padding?: string;
}

export function Footer({className, padding}: Props) {
  const year = new Date().getFullYear();
  const {branding} = useSettings();
  // Footer appearance from the active theme options (style + colors). Falls back
  // to the theme's own styling when unset, so this stays non-breaking.
  const opts = getAppearance().theme?.options ?? {};
  const footerStyle = (opts as any).footer_style;
  const footerCss = footerStyle && footerStyle !== 'simple'
    ? {backgroundColor: (opts as any).footer_bg, color: (opts as any).footer_text_color}
    : undefined;
  return (
    <footer
      data-footer-style={footerStyle || 'simple'}
      style={footerCss}
      className={clsx(
        'text-sm px-16 md:px-40',
        padding ? padding : 'pb-28 pt-54 md:pb-54',
        className,
      )}
    >
      {/* Footer widget area — renders admin-configured footer blocks (nothing
          until blocks are added to the 'footer' area in the builder). */}
      <BlockArea area="footer" />
      <Menus />
      <div className="items-center justify-between gap-30 text-center text-muted md:flex md:text-left">
        <Trans
          message="Copyright © :year :name, All Rights Reserved"
          values={{year, name: branding.site_name}}
        />
        <div>
          <ThemeSwitcher />
          <LocaleSwitcher />
        </div>
      </div>
      {/* Development credit, centered on its own row under the copyright. */}
      <div className="mt-14 text-center text-xs text-muted">
        <Trans
          message="Design and development by <a>RISHA CODE</a>"
          values={{
            a: text => (
              <a
                href="https://rishacode.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary hover:underline"
              >
                {text}
              </a>
            ),
          }}
        />
      </div>
    </footer>
  );
}

/**
 * Four columns: the brand (short pitch, socials, account buttons), then one
 * column per group of the "footer" menu (Admin → Settings → Menus). A flat
 * footer menu — items with no group — becomes a single "Masertak" column, so
 * an unedited menu still renders. Social links come from the
 * "footer-secondary" menu and are drawn with the networks' real logos.
 *
 * Labels are translated: the old footer printed them raw, so an Arabic page
 * ended in "Privacy Policy · Terms of Service".
 */
function Menus() {
  const primaryMenu = useCustomMenu('footer');
  const secondaryMenu = useCustomMenu('footer-secondary');
  const {isLoggedIn} = useAuth();
  const isDarkMode = useIsDarkMode();

  const items = primaryMenu?.items ?? [];
  const groups = items.filter(i => i.type === 'group');
  const loose = items.filter(i => i.type !== 'group');
  const columns: {label: string; items: MenuItemConfig[]}[] = [
    ...groups.map(g => ({label: g.label, items: g.children ?? []})),
    ...(loose.length ? [{label: 'Masertak', items: loose}] : []),
  ];

  const socials = (secondaryMenu?.items ?? [])
    .map(item => ({
      item,
      network:
        detectSocialNetwork(item.action) ||
        detectSocialNetwork(item.label) ||
        (typeof item.icon === 'string' ? item.icon : null),
    }))
    .filter(s => s.network);

  if (!columns.length && !socials.length) return null;

  return (
    <div className="mb-24 grid gap-32 border-b pb-32 sm:grid-cols-2 lg:grid-cols-4">
      <div className="space-y-14">
        <Logo logoType="wide" color={isDarkMode ? 'light' : 'dark'} size="h-36" />
        <p className="max-w-xs text-sm leading-relaxed text-muted">
          <Trans message="Scholarships, universities, courses and the Masertak team to apply with you — all in one place." />
        </p>
        {socials.length > 0 && (
          <div>
            <div className="mb-8 text-xs font-semibold text-muted">
              <Trans message="Follow us" />
            </div>
            <div className="flex flex-wrap gap-10">
              {socials.map(({item, network}) => (
                <BrandSocialIcon key={item.id} network={network!} href={item.action} size={44} />
              ))}
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-8 pt-2">
          {isLoggedIn ? (
            <Button variant="flat" color="primary" size="sm" className="rounded-full" elementType={Link} to="/student">
              <Trans message="My dashboard" />
            </Button>
          ) : (
            <>
              <Button variant="flat" color="primary" size="sm" className="rounded-full" elementType={Link} to="/register">
                <Trans message="Create a free account" />
              </Button>
              <Button variant="outline" size="sm" className="rounded-full" elementType={Link} to="/login">
                <Trans message="Sign in" />
              </Button>
            </>
          )}
        </div>
      </div>

      {columns.slice(0, 3).map(column => (
        <nav key={column.label} aria-label={column.label}>
          <h3 className="mb-12 text-sm font-bold text-main">
            <Trans message={column.label} />
          </h3>
          <ul className="space-y-8">
            {column.items.map(item => (
              <li key={item.id}>
                {item.type === 'link' ? (
                  <a
                    href={item.action}
                    target={item.target || '_blank'}
                    rel="noopener noreferrer"
                    className="text-sm text-muted transition hover:text-primary"
                  >
                    <Trans message={item.label} />
                  </a>
                ) : (
                  <Link to={item.action} target={item.target} className="text-sm text-muted transition hover:text-primary">
                    <Trans message={item.label} />
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>
      ))}
    </div>
  );
}

function ThemeSwitcher() {
  const {themes} = useSettings();
  const {selectedTheme, selectTheme} = useThemeSelector();
  if (!selectedTheme || !themes?.user_change) return null;

  return (
    <Button
      variant="text"
      startIcon={selectedTheme.is_dark ? <DarkModeIcon /> : <LightbulbIcon />}
      onClick={() => {
        if (selectedTheme.is_dark) {
          selectTheme('light');
        } else {
          selectTheme('dark');
        }
      }}
    >
      {selectedTheme.is_dark ? (
        <Trans message="Light mode" />
      ) : (
        <Trans message="Dark mode" />
      )}
    </Button>
  );
}
